<template>
  <div class="data-management well-page qaqc-overview-page">
    <QAQCOverviewShimmer v-if="loading" />
    <UnifiedQAQCDashboard
      v-else-if="mappedDashboard"
      :summary-stats="summaryStats"
      :qc-status-overview="qcStatusOverview"
      :qc-status-panel="qcStatusPanel"
      :active-test-tab="activeTestTab"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :distribution-plot-options="distributionPlotOptions"
      :selected-plot-id="selectedPlotId"
      :distribution-data="distributionData"
      :sample-ids="sampleIds"
      :selected-sample-ids="selectedSampleIds"
      :consistency-cross-test-rows="consistencyCrossTestRows"
      :consistency-ordering-rows="consistencyOrderingRows"
      :equilibrium-plot-rows="equilibriumPlotRows"
      :dl-material-balance-plot-options="dlMaterialBalancePlotOptions"
      :selected-material-balance-plot-id="selectedMaterialBalancePlotId"
      :dl-material-balance-distribution-data="dlMaterialBalanceDistributionData"
      :dl-feed-deviation-data="dlFeedDeviationData"
      @update:active-test-tab="activeTestTab = $event"
      @update:selected-plot-id="selectedPlotId = $event"
      @update:selected-material-balance-plot-id="selectedMaterialBalancePlotId = $event"
      @update:selected-sample-ids="selectedSampleIds = $event"
    />
    <div v-else-if="!loading && error" class="tabs-no-test-div timeout">
      <div class="text-center">
        {{ error }}
        <v-icon flat @click="fetchOverviewData()" color="blue-darken-3" icon="mdi-reload" size="large"></v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import UnifiedQAQCDashboard from "@/components/layout/QAQC/Overview/UnifiedQAQCDashboard.vue";
import QAQCOverviewShimmer from "@/components/layout/QAQC/Overview/QAQCOverviewShimmer.vue";
import { fetchQaqcDashboard, mapDashboardToOverview } from "./qaqcDashboardApi.js";
import {
  mapDistributionPlotToViewData,
  emptyDistributionViewData,
  DL_MATERIAL_BALANCE_PLOT_ORDER,
  DL_FEED_DEVIATION_PLOT_NAMES,
} from "@/components/layout/QAQC/Overview/distributionPlotMapper.js";

function distPlotToData(plot, selectedId) {
  if (!plot || plot.Name !== selectedId) return null;
  return mapDistributionPlotToViewData(plot);
}

export default {
  name: "QAQCOverview",
  components: {
    UnifiedQAQCDashboard,
    QAQCOverviewShimmer,
  },
  data() {
    return {
      loading: false,
      error: null,
      mappedDashboard: null,
      activeTestTab: "oilCce",
      selectedPlotId: "",
      selectedMaterialBalancePlotId: "",
      selectedSampleIds: [],
    };
  },
  computed: {
    summaryStats() {
      return this.mappedDashboard?.summaryStats || this.defaultSummaryStats();
    },
    qcStatusOverview() {
      return this.mappedDashboard?.qcStatusOverview || this.defaultQcStatusOverview();
    },
    qcStatusPanel() {
      return this.mappedDashboard?.qcStatusPanel || this.defaultQcStatusPanel();
    },
    tabData() {
      const byTab = this.mappedDashboard?.byTab || {};
      return byTab[this.activeTestTab] || {};
    },
    scorecardRows() {
      return this.tabData.scorecardRows || [];
    },
    scorecardSummary() {
      return this.tabData.scorecardSummary || this.defaultScorecardSummary();
    },
    validationMatrixRows() {
      return this.tabData.validationMatrixRows || [];
    },
    validationMatrixTitle() {
      return this.tabData.validationMatrixTitle || "Validation Pass Rate Matrix";
    },
    validationMatrixDescription() {
      return this.tabData.validationMatrixDescription || "Percentage of all samples passing each validation check";
    },
    consistencyCrossTestRows() {
      return this.tabData.consistencyCrossTestRows || [];
    },
    consistencyOrderingRows() {
      return this.tabData.consistencyOrderingRows || [];
    },
    equilibriumPlotRows() {
      return this.tabData.equilibriumPlotRows || [];
    },
    distributionPlotOptions() {
      return this.tabData.distributionPlotOptions || [];
    },
    distributionData() {
      const plots = this.tabData.distributionPlots || [];
      const firstId = this.distributionPlotOptions[0]?.id;
      const plotId = this.selectedPlotId || firstId;
      if (!plotId || !plots.length) return this.defaultDistributionData();
      const plot = plots.find((p) => p.Name === plotId);
      const data = plot ? distPlotToData(plot, plotId) : null;
      return data || this.defaultDistributionData();
    },
    dlDistributionPlots() {
      return this.tabData.distributionPlots || [];
    },
    dlMaterialBalancePlotOptions() {
      if (this.activeTestTab !== "dlTests") return [];
      const plots = this.dlDistributionPlots;
      return DL_MATERIAL_BALANCE_PLOT_ORDER.map((id) => plots.find((p) => p.Name === id))
        .filter(Boolean)
        .map((p) => ({ id: p.Name, label: p.DisplayName || p.Name }));
    },
    dlMaterialBalanceDistributionData() {
      if (this.activeTestTab !== "dlTests") return emptyDistributionViewData();
      const id = this.selectedMaterialBalancePlotId;
      if (!id) return emptyDistributionViewData();
      const plot = this.dlDistributionPlots.find((p) => p.Name === id);
      return plot ? mapDistributionPlotToViewData(plot) : emptyDistributionViewData();
    },
    dlFeedDeviationData() {
      if (this.activeTestTab !== "dlTests") return emptyDistributionViewData();
      const plots = this.dlDistributionPlots;
      const plot = DL_FEED_DEVIATION_PLOT_NAMES.map((n) => plots.find((p) => p.Name === n)).find(Boolean);
      return plot ? mapDistributionPlotToViewData(plot) : emptyDistributionViewData();
    },
    sampleIds() {
      return this.mappedDashboard?.sampleIds || [];
    },
  },
  watch: {
    distributionPlotOptions: {
      immediate: true,
      handler(opts) {
        if (opts.length) {
          const hasCurrent = opts.some((o) => o.id === this.selectedPlotId);
          if (!hasCurrent || !this.selectedPlotId) this.selectedPlotId = opts[0].id;
        }
      },
    },
    activeTestTab() {
      const opts = this.distributionPlotOptions;
      if (opts.length) this.selectedPlotId = opts[0].id;
      if (this.activeTestTab === "dlTests") {
        const mb = this.dlMaterialBalancePlotOptions;
        if (mb.length) this.selectedMaterialBalancePlotId = mb[0].id;
        else this.selectedMaterialBalancePlotId = "";
      }
    },
    dlMaterialBalancePlotOptions: {
      immediate: true,
      handler(opts) {
        if (this.activeTestTab !== "dlTests") return;
        if (!opts.length) {
          this.selectedMaterialBalancePlotId = "";
          return;
        }
        const has = opts.some((o) => o.id === this.selectedMaterialBalancePlotId);
        if (!has) this.selectedMaterialBalancePlotId = opts[0].id;
      },
    },
  },
  mounted() {
    this.fetchOverviewData();
  },
  methods: {
    defaultSummaryStats() {
      return {
        totalTests: "-",
        totalTestsChange: "",
        cceTests: "-",
        cceTestsPercent: "",
        dlTests: "-",
        dlTestsPercent: "",
        passRate: "-",
      };
    },
    defaultQcStatusOverview() {
      return {
        categories: ["Passed", "Failed", "Needs Review"],
        series: [
          { name: "CCE Tests", color: "#2298D5", values: [0, 0, 0] },
          { name: "DL Tests", color: "#F97316", values: [0, 0, 0] },
        ],
      };
    },
    defaultQcStatusPanel() {
      return {
        passed: { count: "-", total: "-", percent: "-" },
        needsReview: { count: "-", total: "-", percent: "-" },
        failed: { count: "-", total: "-", percent: "-" },
        subtitle: "Current QC validation results",
      };
    },
    defaultScorecardSummary() {
      return {
        overallPassRate: "-",
        passedQc: "-",
        pendingQc: "-",
        failed: "-",
      };
    },
    defaultDistributionData() {
      return emptyDistributionViewData();
    },
    fetchOverviewData() {
      this.loading = true;
      this.error = null;
      fetchQaqcDashboard()
        .then((data) => {
          this.mappedDashboard = mapDashboardToOverview(data);
        })
        .catch((err) => {
          this.error = err?.message || "Failed to load QA/QC dashboard";
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
@import "../../assets/Styling/qaqc.css";
</style>
